import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PermissionsContext = createContext({
  role: 'viewer',
  permissions: {},
  hasModuleAccess: () => false,
  loading: true
});

export const usePermissions = () => useContext(PermissionsContext);

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();
  const [role, setRole] = useState('viewer');
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user) {
        setLoading(false);
        setRole('viewer');
        setPermissions(getDefaultPermissions('viewer'));
        return;
      }

      // EMERGENCY FIX: Hardcode super admin for platform owner
      if (user.email === 'mithun.kadur@logicalblocks.com') {
        console.log('[Permissions] Platform owner detected - granting super_admin');
        setRole('super_admin');
        setPermissions(getDefaultPermissions('super_admin'));
        setLoading(false);
        return;
      }

      try {
        console.log('[Permissions] Fetching role for user:', user.email);
        
        // Fetch user role
        const roleResponse = await axios.get(`${BACKEND_URL}/api/user/role`, {
          withCredentials: true
        });
        
        console.log('[Permissions] Role response:', roleResponse.data);
        
        const fetchedRole = roleResponse.data.role || 'viewer';
        setRole(fetchedRole);

        console.log('[Permissions] User role:', fetchedRole);

        // Set default permissions based on role
        const defaultPermissions = getDefaultPermissions(fetchedRole);
        setPermissions(defaultPermissions);

      } catch (error) {
        console.error('[Permissions] Error fetching role:', error);
        console.error('[Permissions] Error details:', error.response?.data);
        
        // CRITICAL FIX: On error, default to viewer permissions
        // This prevents showing restricted features if role fetch fails
        setRole('viewer');
        setPermissions(getDefaultPermissions('viewer'));
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [user]);

  const getDefaultPermissions = (userRole) => {
    // Super admin has full control of everything
    if (userRole === 'super_admin') {
      return {
        dashboard: 'full_control',
        trade_analytics: 'full_control',
        insurance: 'full_control',
        marketplace: 'full_control',
        integrations: 'full_control'
      };
    }

    // Company admin has full control except integrations
    if (userRole === 'company_admin') {
      return {
        dashboard: 'full_control',
        trade_analytics: 'full_control',
        insurance: 'full_control',
        marketplace: 'full_control',
        integrations: 'edit'
      };
    }

    // Manager has edit access
    if (userRole === 'manager') {
      return {
        dashboard: 'view',
        trade_analytics: 'edit',
        insurance: 'edit',
        marketplace: 'edit',
        integrations: 'view'
      };
    }

    // Operator has specific access
    if (userRole === 'operator') {
      return {
        dashboard: 'view',
        trade_analytics: 'view',
        insurance: 'view',
        marketplace: 'edit',
        integrations: 'no_access'
      };
    }

    // Viewer (default) - read-only
    return {
      dashboard: 'view',
      trade_analytics: 'view',
      insurance: 'view',
      marketplace: 'view',
      integrations: 'no_access'
    };
  };

  const hasModuleAccess = (module, requiredLevel = 'view') => {
    const permissionHierarchy = {
      'no_access': 0,
      'view': 1,
      'edit': 2,
      'full_control': 3
    };

    const userLevel = permissions[module] || 'no_access';
    const userLevelInt = permissionHierarchy[userLevel] || 0;
    const requiredLevelInt = permissionHierarchy[requiredLevel] || 1;

    return userLevelInt >= requiredLevelInt;
  };

  return (
    <PermissionsContext.Provider value={{ role, permissions, hasModuleAccess, loading }}>
      {children}
    </PermissionsContext.Provider>
  );
};
